/**
 * Custom hook for managing user bookmarks in books
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../config/api';

export interface Bookmark {
  id: string;
  user_id: string;
  book_id: string;
  page_number: number;
  label: string | null;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface BookmarkCreate {
  page_number: number;
  label?: string | null;
  color?: string;
}

export interface BookmarkUpdate {
  label?: string | null;
  color?: string;
}

export function useBookmarks(bookId: string) {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all bookmarks for the book
  const fetchBookmarks = useCallback(async () => {
    if (!bookId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/books/${bookId}/bookmarks`);
      setBookmarks(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch bookmarks');
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Create a new bookmark
  const createBookmark = useCallback(async (data: BookmarkCreate) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post(`/api/books/${bookId}/bookmarks`, data);
      setBookmarks(prev => [...prev, response.data].sort((a, b) => a.page_number - b.page_number));
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to create bookmark';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Update a bookmark
  const updateBookmark = useCallback(async (bookmarkId: string, data: BookmarkUpdate) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.patch(`/api/books/${bookId}/bookmarks/${bookmarkId}`, data);
      setBookmarks(prev =>
        prev.map(b => (b.id === bookmarkId ? response.data : b))
      );
      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to update bookmark';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Delete a bookmark
  const deleteBookmark = useCallback(async (bookmarkId: string) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/api/books/${bookId}/bookmarks/${bookmarkId}`);
      setBookmarks(prev => prev.filter(b => b.id !== bookmarkId));
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to delete bookmark';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Toggle bookmark for a page (create if not exists, delete if exists)
  const toggleBookmark = useCallback(async (pageNumber: number, label?: string, color?: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post(`/api/books/${bookId}/bookmarks/toggle`, {
        page_number: pageNumber,
        label: label || null,
        color: color || 'blue'
      });

      if (response.data.action === 'created') {
        setBookmarks(prev =>
          [...prev, response.data.bookmark].sort((a, b) => a.page_number - b.page_number)
        );
      } else {
        setBookmarks(prev => prev.filter(b => b.page_number !== pageNumber));
      }

      return response.data;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || 'Failed to toggle bookmark';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  // Check if a page is bookmarked
  const isPageBookmarked = useCallback((pageNumber: number) => {
    return bookmarks.some(b => b.page_number === pageNumber);
  }, [bookmarks]);

  // Get bookmark for a specific page
  const getBookmarkForPage = useCallback((pageNumber: number) => {
    return bookmarks.find(b => b.page_number === pageNumber);
  }, [bookmarks]);

  // Load bookmarks when bookId changes
  useEffect(() => {
    if (bookId) {
      fetchBookmarks();
    }
  }, [bookId, fetchBookmarks]);

  return {
    bookmarks,
    loading,
    error,
    fetchBookmarks,
    createBookmark,
    updateBookmark,
    deleteBookmark,
    toggleBookmark,
    isPageBookmarked,
    getBookmarkForPage
  };
}
